import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Mail, RefreshCw, LogOut } from 'lucide-react';

export function EmailVerificationRequired() {
  const { t } = useTranslation();
  const { firebaseUser, resendVerificationEmail, refreshEmailVerification, signOut } = useAuth();
  const [resending, setResending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const handleResend = async () => {
    setResending(true);
    try {
      await resendVerificationEmail();
      toast.success(t('auth.verificationEmailSent', 'Email de verificación enviado'));
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      if (error.code === 'auth/too-many-requests') {
        toast.error(t('auth.tooManyRequests', 'Demasiados intentos. Espera unos minutos.'));
      } else {
        toast.error(t('auth.errorSendingEmail', 'Error al enviar el email'));
      }
    } finally {
      setResending(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const verified = await refreshEmailVerification();
      if (verified) {
        toast.success(t('auth.emailVerified', 'Email verificado correctamente'));
        // La app se actualizará automáticamente
      } else {
        toast.info(t('auth.emailNotYetVerified', 'El email aún no ha sido verificado'));
      }
    } catch (error) {
      console.error('Error refreshing verification status:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-surface-2 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-surface rounded-2xl shadow-xl p-8 text-center">
        <div className="mb-6">
          <div className="mx-auto w-20 h-20 bg-info/10 rounded-full flex items-center justify-center mb-6">
            <Mail className="w-10 h-10 text-info" />
          </div>
          <h1 className="text-2xl font-bold text-ink mb-3">
            {t('auth.verifyYourEmail', 'Verifica tu email')}
          </h1>
          <p className="text-ink-muted mb-2">
            {t('auth.verificationRequired', 'Para continuar usando la aplicación, necesitas verificar tu email.')}
          </p>
          <p className="text-sm text-ink-subtle text-ink-muted">
            {t('auth.verificationSentTo', 'Hemos enviado un enlace de verificación a:')}
          </p>
          <p className="text-sm font-semibold text-ink mt-1">
            {firebaseUser?.email}
          </p>
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleRefresh}
            isLoading={refreshing}
            variant="primary"
            className="w-full"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('auth.alreadyVerified', 'Ya verifiqué mi email')}
          </Button>

          <Button
            onClick={handleResend}
            isLoading={resending}
            variant="outline"
            className="w-full"
          >
            {t('auth.resendVerificationEmail', 'Reenviar email de verificación')}
          </Button>

          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center gap-2 py-2 text-sm text-ink-subtle hover:text-ink  transition-colors"
          >
            <LogOut className="w-4 h-4" />
            {t('auth.useAnotherAccount', 'Usar otra cuenta')}
          </button>
        </div>

        <div className="mt-8 pt-6 border-t border-hairline">
          <p className="text-xs text-ink-subtle">
            {t('auth.checkSpamFolder', 'Si no ves el email, revisa tu carpeta de spam')}
          </p>
        </div>
      </div>
    </div>
  );
}
